import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
// Import context from 3 levels up relative to components/configuracion/Accesos/
import { SesionContext } from "../../../context/System/SesionContext";
import getParamsEnv from "../../../utils/getParamsEnv"; // Import env helper

const ConfigAccesos = () => {
    const [roles, setRoles] = useState([]);
    const [menus, setMenus] = useState([]);
    const [accessControls, setAccessControls] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [loading, setLoading] = useState(false);

    // Expand/Collapse state
    const [expandedSections, setExpandedSections] = useState(new Set());

    // Get API_URL_BASE
    const { API_URL_BASE } = getParamsEnv();

    // Access to session context.
    const { tokenSeguridad, usuarioData } = useContext(SesionContext);

    // Removed hardcoded SuperAdmin lock. 
    // All roles must be configurable via DB.

    useEffect(() => {
        if (tokenSeguridad) {
            fetchMatrix();
        }
    }, [tokenSeguridad]);

    const fetchMatrix = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_URL_BASE}accesscontrol/matrix`, {
                headers: { Authorization: `Bearer ${tokenSeguridad}` },
            });
            setRoles(data.roles);
            setMenus(data.menus);
            setAccessControls(data.accessControls);

            if (data.roles.length > 0) {
                setSelectedRole(prev => {
                    if (prev && data.roles.find(r => r.roleid === prev)) return prev;
                    return data.roles[0].roleid;
                });
            }

            // Auto-expand all top-level sections on load
            const initialExpanded = new Set();
            data.menus.forEach(m => {
                if (!m.menupath || m.menupath === '0') {
                    initialExpanded.add(m.menuid);
                }
            });
            setExpandedSections(initialExpanded);

            setLoading(false);
        } catch (error) {
            console.error("Error fetching access matrix:", error);
            setLoading(false);
        }
    };

    const toggleSection = (menuId) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(menuId)) {
                next.delete(menuId);
            } else {
                next.add(menuId);
            }
            return next;
        });
    };

    // Local state update ONLY (No Auto-Save)
    const handlePermissionChange = (menuId, type, currentValue) => {
        if (!selectedRole) return;

        const newValue = !currentValue;

        // Optimistic update
        const updatedAccessControls = [...accessControls];
        let existingAccess = updatedAccessControls.find(
            (ac) => ac.roleidx === selectedRole && ac.menuid === menuId
        );

        if (!existingAccess) {
            // Create mock entry if it doesn't exist
            existingAccess = {
                roleidx: selectedRole,
                menuid: menuId,
                canview: false,
                canedit: false,
                candeletex: false,
            };
            updatedAccessControls.push(existingAccess);
        }

        // Apply Change
        existingAccess[type] = newValue;

        // Dependency Logic:
        if (type === 'canview' && newValue === false) {
            existingAccess.canedit = false;
            existingAccess.candeletex = false;
        }
        if ((type === 'canedit' || type === 'candeletex') && newValue === true) {
            existingAccess.canview = true;
        }

        setAccessControls(updatedAccessControls);
    };

    // New: Batch Save Function
    const saveChanges = async () => {
        if (!selectedRole) return;

        try {
            setLoading(true);

            // Filter permissions for the selected role ONLY
            const rolePermissions = accessControls.filter(ac => ac.roleidx === selectedRole);

            await axios.post(
                `${API_URL_BASE}accesscontrol/batch`,
                {
                    RoleId: selectedRole,
                    permissions: rolePermissions
                },
                {
                    headers: { Authorization: `Bearer ${tokenSeguridad}` },
                }
            );

            alert("Cambios guardados correctamente.");
            await fetchMatrix(); // Refresh from DB to confirm integrity
        } catch (error) {
            console.error("Error saving changes:", error);
            alert("Error al guardar los cambios.");
            setLoading(false);
        }
    };

    const getPermission = (menuId, type) => {
        const access = accessControls.find(
            (ac) => ac.roleidx === selectedRole && ac.menuid === menuId
        );
        return access ? access[type] : false;
    };
    // Recursive function to build tree
    const buildMenuTree = (menuList) => {
        const map = {};
        const roots = [];

        // Initialize map
        menuList.forEach(item => {
            map[item.menuid] = { ...item, children: [] };
        });

        // Build tree
        menuList.forEach(item => {
            // If item has a registered parent, add to parent's children
            if (item.menupath && item.menupath !== '0' && map[item.menupath]) {
                map[item.menupath].children.push(map[item.menuid]);
            } else {
                // Otherwise it's a root
                roots.push(map[item.menuid]);
            }
        });

        return roots.sort((a, b) => (a.ordermenu || 0) - (b.ordermenu || 0));
    };

    const treeData = React.useMemo(() => buildMenuTree(menus), [menus]);

    // Recursive Row Component
    const MenuRow = ({ menu, level = 0 }) => {
        const isParent = menu.children && menu.children.length > 0;
        const isExpanded = expandedSections.has(menu.menuid);
        const paddingLeft = level * 20 + 24;

        const isSection = level === 0;

        // Dependency Check: If View is False, Edit/Delete should be disabled ideally, 
        // to strictly enforce the "Cannot have Edit without View" rule in UI too.
        // We do this via disabling logic below.
        const canView = getPermission(menu.menuid, 'canview');

        return (
            <React.Fragment>
                <tr className={`transition-colors duration-150 group border-b border-gray-100 last:border-0 ${isSection ? 'bg-slate-50/80' : 'hover:bg-amber-50/30'}`}>
                    <td className="py-3 pr-6" style={{ paddingLeft: `${paddingLeft}px` }}>
                        <div className="flex items-center">
                            {/* Line Guides */}
                            {level > 0 && (
                                <div className="absolute left-6 w-px bg-gray-200 h-full -top-1/2" style={{ left: `${level * 20 + 4}px` }}></div>
                            )}

                            {/* Toggle / Icon */}
                            <div className={`mr-3 relative z-10 ${isSection ? 'text-slate-800' : 'text-slate-400'}`}>
                                {isParent ? (
                                    <button
                                        onClick={() => toggleSection(menu.menuid)}
                                        className="p-1 hover:bg-slate-200 rounded transition-colors focus:outline-none"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1.5 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                )}
                            </div>

                            <div className="flex flex-col select-none cursor-pointer" onClick={() => isParent && toggleSection(menu.menuid)}>
                                <span className={`font-medium ${isSection ? 'text-slate-800 text-base' : 'text-slate-600 text-sm'}`}>
                                    {menu.menuname}
                                </span>
                                {isSection && (
                                    <span className="text-[10px] text-slate-400 font-normal uppercase tracking-wider">
                                        Sección
                                    </span>
                                )}
                            </div>
                        </div>
                    </td>
                    <td className="px-6 py-3 text-center">
                        <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                            {menu.menuid}
                        </span>
                    </td>
                    {/* Render Checkboxes */}
                    {['canview', 'canedit', 'candeletex'].map((permType, idx) => {
                        const colorClass = idx === 0 ? 'blue' : idx === 1 ? 'amber' : 'red';

                        // Determine Disable State
                        let isDisabled = false;

                        return (
                            <td key={permType} className="px-6 py-3 text-center">
                                <label className={`inline-flex items-center justify-center relative group cursor-pointer`}>
                                    <input
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={getPermission(menu.menuid, permType) || false}
                                        onChange={() =>
                                            handlePermissionChange(
                                                menu.menuid,
                                                permType,
                                                getPermission(menu.menuid, permType)
                                            )
                                        }
                                    />
                                    <div className={`w-9 h-5 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-${colorClass}-300 dark:peer-focus:ring-${colorClass}-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-${colorClass}-600`}></div>
                                </label>
                            </td>
                        );
                    })}
                </tr>
                {/* Recursively render children ONLY if expanded */}
                {isParent && isExpanded && menu.children.map(child => (
                    <MenuRow key={child.menuid} menu={child} level={level + 1} />
                ))}
            </React.Fragment>
        );
    };

    return (
        <div className="p-6 bg-slate-50 min-h-screen">
            <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                {/* Header */}
                <div className="p-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-200 to-amber-500">
                            Matriz de Accesos
                        </h1>
                        <p className="text-gray-400 mt-1 text-sm">
                            Configure la visibilidad y permisos del menú para cada rol.
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={saveChanges}
                            disabled={loading || !selectedRole}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium text-sm transition-all shadow-md 
                                ${loading || !selectedRole
                                    ? 'bg-gray-600 cursor-not-allowed opacity-50'
                                    : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-amber-500/20 active:scale-95'}`}
                        >
                            {loading ? (
                                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                            )}
                            Guardar Cambios
                        </button>
                        <div className="flex items-center space-x-3 bg-gray-800 p-1.5 rounded-lg border border-gray-700 shadow-inner">
                            <span className="text-gray-400 text-xs font-bold uppercase px-2 tracking-wide">Rol:</span>
                            <select
                                className="bg-gray-700 text-white text-sm px-4 py-2 rounded-md focus:ring-1 focus:ring-amber-500 border-none outline-none transition-all cursor-pointer hover:bg-gray-600 font-medium"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                            >
                                {roles.map((role) => (
                                    <option key={role.roleid} value={role.roleid}>
                                        {role.rolename}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table Container */}
                <div className="overflow-x-auto min-h-[500px]">
                    {loading ? (
                        <div className="p-16 text-center flex flex-col items-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mb-4"></div>
                            <p className="text-slate-500 text-sm font-medium">Cargando estructura de menús...</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-100 border-b border-gray-200">
                                    <th className="px-6 py-4 font-bold text-slate-700 text-xs uppercase tracking-wider pl-8">Estructura del Menú</th>
                                    <th className="px-6 py-4 font-bold text-slate-700 text-xs uppercase tracking-wider text-center w-20">ID</th>
                                    <th className="px-6 py-4 font-bold text-slate-700 text-xs uppercase tracking-wider text-center w-28">
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-blue-600">Lectura</span>
                                            <span className="text-[10px] text-slate-400 font-normal lowercase">(Ver)</span>
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 font-bold text-slate-700 text-xs uppercase tracking-wider text-center w-28">
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-amber-600">Escritura</span>
                                            <span className="text-[10px] text-slate-400 font-normal lowercase">(Editar)</span>
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 font-bold text-slate-700 text-xs uppercase tracking-wider text-center w-28">
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-red-600">Eliminar</span>
                                            <span className="text-[10px] text-slate-400 font-normal lowercase">(Borrar)</span>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {treeData.length > 0 ? (
                                    treeData.map((menu) => (
                                        <MenuRow key={menu.menuid} menu={menu} level={0} />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-slate-400 italic">
                                            No se encontraron menús.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfigAccesos;
